import {
  AiRedoIcon,
  IconButton,
  IconButtonColorEnum,
  IconButtonSizeEnum,
  IconSizeEnum,
  Tooltip,
} from '@acceso/design-system';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { RelaunchEnrichmentsModal } from '@/components/RelaunchEnrichmentsModal/RelaunchEnrichmentsModal';
import { ReviewStateField } from '@/components/ReviewStateField/ReviewStateField';
import { CLIP_REVIEW_STATES, SEGMENT_REVIEW_STATES, TReviewStates } from '@/constants';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { usePostClipsBatch } from '@/features/clips/api/post-clips-batch';
import { useDisclosureWithData } from '@/hooks/useDisclosureWithData';
import { useSelectedItemsStore } from '@/stores/selected-items';

import { usePostSegmentsBatch } from '../api/post-segments-batch';
import { ReviewStateEnum } from '../types';

type TSegmentsCardBulkActionsProps = {
  clips?: boolean;
};
export function SegmentsCardBulkActions({ clips }: TSegmentsCardBulkActionsProps) {
  const store = useSelectedItemsStore();
  const { t } = useTranslation();
  const relaunchDialog = useDisclosureWithData();
  const { mutate: processSegmentsBatch, isPending: isProcessingSegments } = usePostSegmentsBatch({
    config: {
      onSuccess: () => {
        store.dispatch({ type: 'resetIds' });
      },
    },
  });
  const { mutate: processClipsBatch, isPending: isProcessingClips } = usePostClipsBatch({
    config: {
      onSuccess: () => {
        // `reset`, not `resetIds`: on /app/review/clips the selection follows the
        // list while "select all" is on, so leaving the flag set would fill the
        // selection back up as soon as the updated clips reach the list.
        store.dispatch({ type: 'reset' });
      },
    },
  });
  const hasSelection = store.include.length > 0;
  const { segmentItems } = useBroadcastScheduleProgram();
  const hasRadioSelected =
    segmentItems?.some(item => store.include.includes(item.id) && item.media_type === 'rts') ||
    false;

  return (
    <div className="flex gap-2">
      <Tooltip help={t('Cambiar estado')} grow>
        <ReviewStateField
          reviewStates={
            (clips ? CLIP_REVIEW_STATES : SEGMENT_REVIEW_STATES) as TReviewStates<ReviewStateEnum>[]
          }
          disabled={!hasSelection || isProcessingSegments || isProcessingClips}
          onChange={value => {
            if (clips) {
              processClipsBatch({
                action: 'update',
                items: store.include.map(id => ({
                  id,
                  review_state: value,
                })),
              });
            } else {
              processSegmentsBatch({
                action: 'update',
                items: store.include.map(id => ({
                  id,
                  review_state: value,
                })),
              });
            }
          }}
        />
      </Tooltip>

      {/* <Tooltip help={t('Descargar')} grow> */}
      {/*   <DownloadIcon disabled={store.include.length === 0} size={IconSizeEnum.SM} /> */}
      {/* </Tooltip> */}

      {!clips && (
        <Tooltip help={t('Relanzar')} grow>
          <IconButton
            onClick={() => {
              relaunchDialog.open();
            }}
            size={IconButtonSizeEnum.XS}
            color={IconButtonColorEnum.NONE_DEFAULT}
            disabled={isProcessingSegments}
          >
            <AiRedoIcon disabled={store.include.length === 0} size={IconSizeEnum.SM} />
          </IconButton>
        </Tooltip>
      )}

      {/* Use react portal to render modal over toolkit and calendar */}
      {createPortal(
        <RelaunchEnrichmentsModal
          isOpen={relaunchDialog.isOpen}
          onClose={relaunchDialog.close}
          onAction={enrichments => {
            processSegmentsBatch({
              action: 'relaunch',
              items: store.include.map(id => ({ id })),
              enrichments,
            });
          }}
          heading={t('Relanzar procesos de enriquecimiento')}
          actionLabel={t('Lanzar procesos')}
          allowAdvertising={false}
          isRadio={hasRadioSelected}
        />,
        document.body
      )}
    </div>
  );
}
